import { z } from 'zod';
import { uuidSchema, addressSchema, decimalAmountSchema } from './common.js';
import { PairStatus, LifecycleStatus } from '../types/enums.js';
/**
 * Dashboard API contracts.
 *
 * Shared by the API and the frontend so the browser validates against exactly
 * the rules the server enforces.
 *
 * Every monetary and token amount crosses the wire as a **string**. Token
 * balances routinely exceed `Number.MAX_SAFE_INTEGER`, and an 18-decimal ETH
 * value cannot be represented exactly as a float. Parsing "0.1" into a float
 * and back is how a portfolio quietly stops matching the wallet.
 */
// --- Pairs -----------------------------------------------------------------
export const pairStatusSchema = z.enum([
    PairStatus.New,
    PairStatus.Tracking,
    PairStatus.Ignored,
    PairStatus.Bought,
    PairStatus.Error,
    PairStatus.Rugged,
]);
/**
 * The statuses an operator may set by hand.
 *
 * `bought` and `rugged` are excluded because both are findings rather than
 * preferences: one means a trade confirmed, the other means the system watched
 * the LP get pulled. Letting either be typed in would turn evidence into an
 * opinion, and `rugged` in particular is terminal — a hand-set one could not be
 * distinguished from a real observation afterwards.
 */
export const operatorSettablePairStatusSchema = z.enum([
    PairStatus.New,
    PairStatus.Tracking,
    PairStatus.Ignored,
    PairStatus.Error,
]);
/**
 * A boolean query flag as `"true"`/`"false"`, left as that literal union
 * rather than transformed to `boolean`.
 *
 * Not `z.coerce.boolean()`: that coerces *any* non-empty string, including
 * the literal text `"false"`, to `true` -- the exact footgun `boolFromEnv`
 * in `packages/shared/src/server/env.ts` already exists to avoid for
 * environment variables. A dashboard filter deserves the same care.
 *
 * Not `.transform((v) => v === 'true')` either: that gives the field a
 * different `_input` and `_output` type, which `validateQuery<T>`'s
 * `ZodType<T>` constraint (`apps/api/src/middleware/validate.ts`) does not
 * tolerate -- it infers `Input = Output = T`, matching every other field in
 * this schema (`quoted` included, via its own `.default(...)`). Callers
 * compare `=== 'true'` explicitly instead.
 */
const boolQueryFlag = z.enum(['true', 'false']).optional();
/**
 * The real `lifecycleStatus` values (Phase 11, Slice 11F/11G, ADR-056), plus
 * one sentinel this API layer interprets specially: `'candidates'` (plural,
 * deliberately distinct from the real enum value `'candidate'`). It does not
 * mean every pair that has not yet been excluded. It positively selects
 * `candidate` or `approval_eligible` after read-time evidence checks. Tab
 * membership, cursor pagination and totals use the same current projection.
 */
export const pairLifecycleFilterSchema = z.enum([
    LifecycleStatus.New,
    LifecycleStatus.Settling,
    LifecycleStatus.Candidate,
    LifecycleStatus.Watch,
    LifecycleStatus.ApprovalEligible,
    LifecycleStatus.SuspectedRug,
    LifecycleStatus.Rugged,
    LifecycleStatus.DeadLiquidity,
    LifecycleStatus.Blocked,
    LifecycleStatus.Stale,
    LifecycleStatus.Archived,
    'candidates',
]);
/** Pair feed query. */
export const pairListQuerySchema = z.object({
    status: pairStatusSchema.optional(),
    /**
     * Filter by the latest risk verdict. `unscored` selects pairs with no signal
     * yet, which is a distinct and useful state rather than an absence.
     */
    verdict: z.enum(['avoid', 'risky', 'watch', 'qualified', 'unscored']).optional(),
    /** Minimum score on the latest signal. Excludes unscored pairs. */
    min_score: z.coerce.number().int().min(0).max(100).optional(),
    /** Only pairs whose quote side is a recognised quote asset. */
    quoted: z
        .enum(['any', 'known', 'unknown'])
        .default('any'),
    /** Case-insensitive match against a token symbol or any address. */
    search: z.string().trim().max(64).optional(),
    // --- Phase 11, Slice 11F: dashboard feed cleanup and filters ------------
    //
    // One query mechanism, per the phase prompt's own instruction: every tab
    // on the dashboard (Candidates, Watch, Approval Eligible, Rugged,
    // Suspected Rug, Dead Liquidity, Blocked, Stale, All) is this one
    // parameter with a different value, not a second endpoint or a second
    // query shape.
    lifecycle_status: pairLifecycleFilterSchema.optional(),
    /** Hide anything with rug evidence: `status='rugged'` (the older, still-authoritative field) or `lifecycleStatus` in (rugged, suspected_rug). */
    hide_rugged: boolQueryFlag,
    /** Hide `liquidityStatus='dead'` or `lifecycleStatus='dead_liquidity'`. */
    hide_dead_liquidity: boolQueryFlag,
    /** Hide `lifecycleStatus='blocked'`. */
    hide_blocked: boolQueryFlag,
    /** Fresh liquidity after read-time expiry and worker-source checks. */
    fresh_liquidity_only: boolQueryFlag,
    /** Current pre-trade eligibility; cached booleans alone cannot pass. */
    approval_eligible_only: boolQueryFlag,
    /** Only pairs on the requesting operator's own watchlist. */
    watchlisted_only: boolQueryFlag,
    /** Only a risk score at or above this implementation's "high score" bar. */
    high_score_only: boolQueryFlag,
    /** Only liquidity at or above this implementation's "high liquidity" bar. */
    high_liquidity_only: boolQueryFlag,
    /** Only pairs connected to an address with `high`/`critical` funding-path provenance (Slice 11E). */
    high_provenance_risk_only: boolQueryFlag,
    /** Only pairs connected to an address tagged `REPEATED_LP_REMOVER`/`PERSISTENT_RUG_ACTOR` (Slice 11D). */
    repeated_lp_remover_only: boolQueryFlag,
    /** Only pairs first seen within the last 30 minutes. */
    new_within_30_minutes: boolQueryFlag,
    limit: z.coerce.number().int().min(1).max(100).default(25),
    /** Opaque cursor from the previous page's `next_cursor`. */
    cursor: z.string().trim().max(200).optional(),
});
export const pairIdParamSchema = z.object({ id: uuidSchema });
export const pairStatusUpdateSchema = z.object({
    // The settable subset, not the full enum. This makes it structurally
    // impossible to hand-set `bought` or `rugged` rather than relying on a check
    // inside the route.
    //
    // Note the asymmetry, which is deliberate: an operator cannot move a pair
    // *into* `rugged`, but can move one *out* of it by setting `tracking`. The
    // system produces the finding; the operator may overrule it, and the audit log
    // records that they did. A terminal state with no escape hatch would mean one
    // false positive permanently writes off a real token.
    status: operatorSettablePairStatusSchema,
});
// --- Watchlist -------------------------------------------------------------
export const watchlistCreateSchema = z.object({
    pair_id: uuidSchema,
    notes: z.string().trim().max(1000).optional(),
});
export const watchlistUpdateSchema = z.object({
    notes: z.string().trim().max(1000).nullable(),
});
// --- Portfolio -------------------------------------------------------------
/**
 * A positive decimal amount.
 *
 * Zero is rejected: a position of nothing is not a position, and allowing it
 * invites divide-by-zero in any future average-cost calculation.
 */
const positiveDecimalSchema = decimalAmountSchema.refine((value) => Number.parseFloat(value) > 0, 'Must be greater than zero');
export const positionCreateSchema = z.object({
    token_address: addressSchema,
    pair_id: uuidSchema.optional(),
    amount: positiveDecimalSchema,
    /** Total ETH spent. Zero is allowed — an airdrop has no cost basis. */
    cost_basis_eth: decimalAmountSchema,
    notes: z.string().trim().max(1000).optional(),
});
export const positionUpdateSchema = z
    .object({
    amount: positiveDecimalSchema.optional(),
    cost_basis_eth: decimalAmountSchema.optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    /** Set true to close the position, false to reopen it. */
    closed: z.boolean().optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'Provide at least one field to update');
// --- Bot status ------------------------------------------------------------
export const killSwitchUpdateSchema = z.object({
    engaged: z.boolean(),
    /** Free-text note recorded in the audit log. */
    reason: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=dashboard.js.map